// Scoped variable taxonomy for the Builder CMS platform (Phase 0, ENG-169).
//
// Every variable a workflow can read is addressed by a CANONICAL KEY of the
// form `scope.path` — e.g. `recipient.monitoring.type`, `session.answers.q1`,
// `manager.first_name`. This module is the ONE place the taxonomy, the key
// grammar, and the evaluation-location matrix live on the TS side.
//
// Storage stays bare-keyed. Scoping is applied at EMISSION (`derive_facts`,
// `computeFacts`, `resolve_variable_values*` prefix their output); content
// rules/tokens and registry rows carry canonical keys, per-recipient data rows
// never do.
//
// The rule evaluators are FROZEN — this module adds no operators and does not
// change `evaluateRule` semantics. It only says which keys may legally appear
// in which position.
/**
 * The five variable scopes.
 *
 * - `recipient` — the person being cared for: profile field_values, derived
 *   facts (`recipient.monitoring.type`), identity autofill, per-assignment
 *   config values. The ONLY scope that drives resolution.
 * - `user` — the viewer right now. Built-in `user.role` is always present
 *   (`caregiver` | `manager` | `anonymous`); everything else is empty when
 *   anonymous.
 * - `manager` — manager identity autofill.
 * - `session` — per-play state: built-ins plus author-declared workflow
 *   variables.
 * - `org` — reserved for the organizations / clinic channel (ENG-153).
 */
export const VARIABLE_SCOPES = ['recipient', 'user', 'manager', 'session', 'org'];
export function isVariableScope(value) {
    return VARIABLE_SCOPES.includes(value);
}
/**
 * Scopes whose values are a stable property of the CARE RECIPIENT, and may
 * therefore drive server-side resolution (variant fork matching, workflow and
 * category `visible_when`).
 *
 * Only `recipient` qualifies. `user` / `manager` / `org` are identity autofill
 * (`drives_resolution = false` in the registry) and `session` is per-play, so
 * none of them may decide which content a recipient receives — variant
 * selection has to be stable across viewers, and the safety canary must not
 * depend on who happens to be looking.
 */
export const RESOLUTION_SCOPES = ['recipient'];
/** Path segments: alphanumerics + underscore, dot-separated. */
const PATH_SEGMENT = /^[a-z0-9_]+$/i;
/**
 * Parse `scope.path` into its parts. Returns null when the key is not a valid
 * canonical key — callers decide whether that is an error or a legacy bare key.
 *
 * Deliberately strict: a bare key with no dot (`first_name`) is NOT canonical
 * and returns null, so the Phase 1 migration can tell migrated content from
 * unmigrated content by parsing alone.
 */
export function parseCanonicalKey(key) {
    const firstDot = key.indexOf('.');
    if (firstDot <= 0 || firstDot === key.length - 1)
        return null;
    const scope = key.slice(0, firstDot);
    const path = key.slice(firstDot + 1);
    if (!isVariableScope(scope))
        return null;
    if (!path.split('.').every((seg) => PATH_SEGMENT.test(seg)))
        return null;
    return { scope, path };
}
/** Build a canonical key from its parts. Inverse of `parseCanonicalKey`. */
export function formatCanonicalKey(scope, path) {
    return `${scope}.${path}`;
}
/** True when `key` parses as a canonical `scope.path` key. */
export function isCanonicalKey(key) {
    return parseCanonicalKey(key) !== null;
}
/** The scope of a canonical key, or null if it isn't one. */
export function scopeOf(key) {
    return parseCanonicalKey(key)?.scope ?? null;
}
// ── Reserved session built-ins ──────────────────────────────────────────────
/**
 * Session keys the ENGINE owns. Authors may READ these but may never declare a
 * workflow variable that collides with one, and may never target one with an
 * action — they are maintained by `advance()` alone.
 *
 * Prefixed families (`session.answers.<answer_key>` etc.) are open-ended, so
 * they are matched by prefix rather than exact name.
 */
export const RESERVED_SESSION_PREFIXES = [
    'answers.',
    'correct.',
    'visits.',
    'attempts.',
];
/** Reserved session keys with no sub-path. */
export const RESERVED_SESSION_KEYS = [
    'restarts',
    /** Reserved for web resume; not yet emitted by the engine. */
    'attempt_number',
];
/**
 * True when `path` (the part after `session.`) is engine-owned.
 * `isReservedSessionPath('answers.q1')` → true.
 * `isReservedSessionPath('strikes')` → false (author-declared).
 */
export function isReservedSessionPath(path) {
    if (RESERVED_SESSION_KEYS.includes(path))
        return true;
    return RESERVED_SESSION_PREFIXES.some((prefix) => path.startsWith(prefix));
}
const ALL_SCOPES = VARIABLE_SCOPES;
/**
 * Scopes legal in each evaluation location.
 *
 * The split is deliberate and load-bearing: the two SERVER-side locations
 * (`fork_match`, `content_visibility`) accept `recipient.*` only. Everything a
 * viewer or a play-through contributes is confined to client + runtime
 * positions, so which content a recipient gets can never vary by who opened it.
 */
export const EVALUATION_MATRIX = {
    fork_match: RESOLUTION_SCOPES,
    content_visibility: RESOLUTION_SCOPES,
    block_visibility: ALL_SCOPES,
    conditional_routing: ALL_SCOPES,
    interpolation: ALL_SCOPES,
    action_target: ['session'],
};
/**
 * Check one variable reference against the matrix.
 * Returns null when allowed, or a violation describing why not.
 *
 * `declaredSessionVars` is required to validate `session.*` references: an
 * undeclared session variable is a violation everywhere, because the engine
 * has no initial value for it.
 */
export function checkVariableUsage(key, location, declaredSessionVars = []) {
    const parsed = parseCanonicalKey(key);
    if (!parsed) {
        return {
            key,
            location,
            reason: `"${key}" is not a canonical scope.path key`,
        };
    }
    const allowed = EVALUATION_MATRIX[location];
    if (!allowed.includes(parsed.scope)) {
        const reason = location === 'action_target'
            ? `only declared session variables may be action targets; "${key}" is ${parsed.scope}-scoped`
            : `${parsed.scope}.* may not be used in ${location} — that position is resolved server-side and must depend only on the care recipient`;
        return { key, location, reason };
    }
    if (parsed.scope === 'session') {
        const reserved = isReservedSessionPath(parsed.path);
        if (location === 'action_target') {
            if (reserved) {
                return {
                    key,
                    location,
                    reason: `"${key}" is an engine-owned session built-in and cannot be assigned`,
                };
            }
            if (!declaredSessionVars.includes(parsed.path)) {
                return {
                    key,
                    location,
                    reason: `session variable "${parsed.path}" is not declared on this workflow`,
                };
            }
            return null;
        }
        // Reads: built-ins are always fine; author variables must be declared.
        if (!reserved && !declaredSessionVars.includes(parsed.path)) {
            return {
                key,
                location,
                reason: `session variable "${parsed.path}" is not declared on this workflow`,
            };
        }
    }
    return null;
}
/** Convenience: true when the reference is legal in that position. */
export function isVariableUsageAllowed(key, location, declaredSessionVars = []) {
    return checkVariableUsage(key, location, declaredSessionVars) === null;
}
// ── Interpolation ───────────────────────────────────────────────────────────
/**
 * `{{token}}` matcher, widened from the legacy `[\w]+` to accept the dots in
 * canonical keys.
 *
 * Not exported as a shared mutable RegExp — `INTERPOLATION_PATTERN_SOURCE` is
 * the source string, so each consumer builds its own instance and no one
 * inherits another caller's `lastIndex`.
 */
export const INTERPOLATION_PATTERN_SOURCE = '\\{\\{([\\w.]+)\\}\\}';
/** A fresh global matcher for `{{token}}`. */
export function interpolationPattern() {
    return new RegExp(INTERPOLATION_PATTERN_SOURCE, 'g');
}
/** Every distinct `{{token}}` key in a string, in first-appearance order. */
export function extractInterpolationKeys(content) {
    const keys = [];
    for (const match of content.matchAll(interpolationPattern())) {
        const key = match[1];
        if (key !== undefined && !keys.includes(key))
            keys.push(key);
    }
    return keys;
}
